import React from "react";
import { TrendingDown, Clock, DollarSign, Users, TrendingUp } from "lucide-react";

const PainPoints = () => {
  const painPoints = [
    {
      icon: TrendingDown,
      problem: "Manual Processes Slow You Down",
      description: "Manual lead tracking and follow-up means opportunities slip through the cracks and response times lag behind competitors",
      color: "var(--accent-orange-200)"
    },
    {
      icon: Clock,
      problem: "Time Wasted on Repetitive Tasks",
      description: "Your team spends valuable hours on administrative work that could be automated, leaving less time for strategic activities",
      color: "var(--accent-blue-200)"
    },
    {
      icon: DollarSign,
      problem: "Inconsistent Follow-Up",
      description: "Without automation, lead follow-up depends on manual effort, leading to missed opportunities and inconsistent customer experience",
      color: "var(--accent-purple-200)"
    },
    {
      icon: Users,
      problem: "Scaling Requires More Staff",
      description: "Growing your business traditionally means hiring more people, increasing overhead and management complexity",
      color: "var(--accent-pink-200)"
    }
  ];

  return (
    <section className="py-20" style={{ background: 'var(--bg-page)' }}>
      <div className="container relative">
        {/* Background Images for Visual Impact */}
        <div className="absolute top-8 right-8 opacity-20 hidden lg:block">
          <img 
            src="https://images.unsplash.com/photo-1758525588907-5a06b0b68462" 
            alt="Business struggle"
            className="w-64 h-48 object-cover rounded-lg"
          />
        </div>
        <div className="absolute bottom-8 left-8 opacity-15 hidden lg:block">
          <img 
            src="https://images.unsplash.com/photo-1758520144667-3041caeff3c1" 
            alt="Overwork exhaustion"
            className="w-56 h-40 object-cover rounded-lg"
          />
        </div>
        <div className="text-center mb-16">
          <div className="hero-announcement mb-6">
            <TrendingUp size={12} style={{ color: 'var(--accent-blue-400)' }} />
            <span>AUTOMATE YOUR BUSINESS OPERATIONS</span>
          </div>
          <h2 className="heading-1 mb-4">
            Common Challenges <span style={{ color: 'var(--accent-blue-400)' }}>AI Can Solve</span>
          </h2>
          <p className="body-large" style={{ 
            maxWidth: '700px', 
            margin: '0 auto',
            color: 'var(--text-secondary)' 
          }}>
            Many businesses face these operational challenges. AI automation can help streamline processes and improve efficiency.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
          {painPoints.map((point, index) => {
            const IconComponent = point.icon;
            
            return (
              <div 
                key={index}
                className="voice-card hover-lift border-l-4"
                style={{ 
                  background: point.color,
                  borderLeftColor: 'var(--accent-blue-400)'
                }}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div 
                    className="p-3 rounded-full flex-shrink-0"
                    style={{ 
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: 'var(--accent-blue-400)'
                    }}
                  >
                    <IconComponent size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="voice-card-title mb-2" style={{ color: 'var(--accent-blue-400)' }}>
                      {point.problem}
                    </h3>
                  </div>
                </div>
                
                <div className="voice-card-description">
                  {point.description}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col items-center gap-4 p-8 rounded-lg max-w-lg mx-auto" style={{ 
            background: 'var(--bg-card)',
            border: '2px solid var(--accent-blue-400)'
          }}>
            <div className="heading-3 mb-2" style={{ color: 'var(--accent-blue-400)' }}>
              See How AI Can Help Your Business
            </div>
            <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>
              Schedule a consultation to learn how our AI agent system can address your specific business challenges
            </p>
            <button className="btn-primary hover-scale text-lg px-8 py-4">
              Schedule Consultation
            </button>
            <div className="caption" style={{ color: 'var(--text-muted)' }}>
              Free consultation • No obligations
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PainPoints;